import { PICK_FRAMES_PER_TILESET, PICK_FRAMES_PER_WORLD } from "../assets/budget.ts";
import { frameId } from "../assets/manifest.ts";
import type { TileRef, TilesetRef } from "../document/types.ts";
import type { EditorCatalogue } from "./catalogue.ts";
import type { PackIndexTileset } from "./pack-index.ts";
import { packOf, wholePacksOf } from "./packs.ts";

/**
 * What the editor shows of a world's tileset entries (ADR-129 decision 3.5). A pack the world holds a whole entry
 * of is packed and listed whole, as before entries could name frames. Of a pack the world has only pulled from,
 * the editor's atlas packs the picked frames with every frame of the strips they start, and the editor's
 * catalogue lists the props whose frames the atlas packs and nothing else. The pack itself is still imported
 * whole for the compile context, so footprints, sort lines, depth and timing follow the pack; only its pictures
 * are held back.
 */

/**
 * What the editor's atlas packs: the tilesets packed whole, the frame ids packed beside them, the picked frames
 * the catalogue does not hold (left out, for a warning: `missing` by frame id and `lost` as the same frames by
 * tileset and tile, which the editor's catalogue carries), and how many picks of which entry lie past the suggested
 * size of an entry or of a world (left out too, so a document a merge left past a cap still builds).
 */
export interface EditorAtlasFrames {
  readonly wholeTilesets: ReadonlySet<string>;
  readonly used: ReadonlySet<string>;
  readonly missing: readonly string[];
  readonly lost: readonly TileRef[];
  readonly past: readonly { readonly tileset: string; readonly frames: number }[];
}

/**
 * Works out what the editor's atlas packs of a world's tileset list. A strip is followed from the pick that
 * starts it whether it loops or plays once, since the palette shows either and the atlas build follows looping
 * animations only.
 *
 * @param tilesets The world's tileset list.
 * @param catalogue Every tileset of the packs the list names, as their importers yield them.
 * @returns The whole tilesets (every catalogue tileset of a pack the list holds a whole entry of, and every whole
 *   entry itself), the frames of the partial entries with their strips, the picks the catalogue lacks and the
 *   picks past `PICK_FRAMES_PER_TILESET` an entry or `PICK_FRAMES_PER_WORLD` a world, counted by entry.
 */
export function editorAtlasFrames(tilesets: readonly TilesetRef[], catalogue: readonly PackIndexTileset[]): EditorAtlasFrames {
  const wholePacks = new Set(wholePacksOf(tilesets));
  const wholeTilesets = new Set<string>();
  for (const tileset of catalogue) {
    if (wholePacks.has(packOf(tileset.id) ?? "")) {
      wholeTilesets.add(tileset.id);
    }
  }
  for (const entry of tilesets) {
    if (entry.frames === undefined) {
      wholeTilesets.add(entry.id);
    }
  }
  const byId = new Map(catalogue.map((tileset) => [tileset.id, tileset]));
  const used = new Set<string>();
  const missing: string[] = [];
  const lost: TileRef[] = [];
  const past: { tileset: string; frames: number }[] = [];
  let picked = 0;
  for (const entry of tilesets) {
    if (entry.frames === undefined || wholeTilesets.has(entry.id)) {
      continue;
    }
    const taken = Math.max(0, Math.min(entry.frames.length, PICK_FRAMES_PER_TILESET, PICK_FRAMES_PER_WORLD - picked));
    picked += taken;
    if (taken < entry.frames.length) {
      past.push({ tileset: entry.id, frames: entry.frames.length - taken });
    }
    const tileset = byId.get(entry.id);
    const frames = new Map((tileset?.frames ?? []).map((frame) => [frame.tile, frame]));
    const tiles = new Set((tileset?.tileset.tiles ?? []).map((tile) => tile.id));
    for (const tile of entry.frames.slice(0, taken)) {
      const frame = frames.get(tile);
      if (frame === undefined && !tiles.has(tile)) {
        missing.push(frameId(entry.id, null, tile));
        lost.push({ tileset: entry.id, tile });
        continue;
      }
      used.add(frameId(entry.id, null, tile));
      if (frame?.strip !== undefined && frame.strip.index === 0) {
        for (const shown of frame.strip.frames) {
          used.add(frameId(entry.id, null, shown));
        }
      }
    }
  }
  return { wholeTilesets, used, missing, lost, past };
}

/**
 * The editor's catalogue of a world with partial entries: the tilesets packed whole with all their props, and of
 * every other tileset only the props whose frames the atlas packs, the tileset itself listed while it has one.
 *
 * @param catalogue The catalogue of the world's packs, each whole.
 * @param wholeTilesets The tilesets the editor's atlas packs whole.
 * @param packed Every frame id the editor's atlas packs beside them: the picks with their strips, and what the
 *   compiled world draws.
 * @param lost The pulled frames their pack does not hold, which the catalogue names so the editor can report
 *   each and offer its removal (ADR-129 decision 3.12).
 * @returns The catalogue to write, the same object when every tileset is whole and no pull is lost.
 */
export function partialCatalogue(catalogue: EditorCatalogue, wholeTilesets: ReadonlySet<string>, packed: ReadonlySet<string>, lost: readonly TileRef[] = []): EditorCatalogue {
  if (lost.length === 0 && catalogue.tilesets.every((tileset) => wholeTilesets.has(tileset.id))) {
    return catalogue;
  }
  const props = catalogue.props.filter((prop) => wholeTilesets.has(prop.tileset) || packed.has(frameId(prop.tileset, null, prop.tile)));
  const held = new Set(props.map((prop) => prop.tileset));
  return { ...catalogue, tilesets: catalogue.tilesets.filter((tileset) => wholeTilesets.has(tileset.id) || (tileset.role === "props" && held.has(tileset.id))), props, ...(lost.length === 0 ? {} : { lostPulls: lost.map((frame) => ({ tileset: frame.tileset, tile: frame.tile })) }) };
}
